import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { IAssignment, IClassroom } from '@/components/staffer/Grading/types';
import { useQuery } from '@tanstack/react-query';
import Axios from 'axios';
import * as Routes from '@/modules/routes';
import useGradingStore from '@/components/staffer/Grading/useGradingStore';

import {
  multipleChoiceQuestionAnswerAttributes,
} from './StudentAnswers/MultipleChoiceQuestionStudentAnswer';
import {
  imcAnswerAttributes,
} from './StudentAnswers/ImageMultipleChoiceQuestionStudentAnswer';

interface IQuestionGradingHooksTypes {
  programId: number;
  userId: number;
  userType: string;
}

const QuestionGradingHooks = (
  { programId, userId, userType } : IQuestionGradingHooksTypes,
) => {
  const { assignmentId, questionId } = useParams();
  const [assignment, setAssignment] = useState<IAssignment>();
  const [classroom, setClassroom] = useState<IClassroom>();
  const params = new URLSearchParams(window.location.search);
  const classroomIdParam = params.get('classroom_id');

  const findCurrentClassroom = (classrooms) => {
    if (classroomIdParam) {
      return classrooms.find(c => c.id === parseInt(classroomIdParam, 10));
    }
    return classrooms.find(c => c.students.some(st => st.submission_id));
  };

  useEffect(() => {
    if (!assignment) return;

    setClassroom(findCurrentClassroom(assignment.classrooms));
  }, [assignment, classroomIdParam]);

  // ASSIGNMENT
  const assignmentQuery = useQuery(
    ['assignment'],
    () => (
      Axios.get(
        Routes.plato_api_assignment_path(
          assignmentId,
          {
            extra_attributes: ['assignment_details', 'classrooms', 'questions', 'question_data_for_navigation'],
            only: [
              'assignment_location',
              'due_datetime',
              'id',
              'max_points',
            ],
          },
        ),
      ).then(res => res.data)
    ),
  );
  useEffect(() => {
    if (assignmentQuery.isLoading) return;

    const result = assignmentQuery.data.assignment;
    setAssignment(result);
    setClassroom(findCurrentClassroom(result.classrooms));
  }, [assignmentQuery.data]);

  // QUESTION ANSWERS
  const studentQuestionAnswerQuery = useQuery(
    ['studentAnswer', classroom?.id],
    () => (
      Axios.get(Routes.plato_api_question_answers_path({
        extra_attributes: [
          ...multipleChoiceQuestionAnswerAttributes,
          ...imcAnswerAttributes,
        ],
        for_question_ids: assignment.questions.map(q => q.id),
        for_student: classroom.students.map(st => st.id),
      }))
        .then(res => res.data)
    ),
    { enabled: (!!assignment && !!classroom) },
  );

  const currentUserQuery = useQuery(
    ['getUser'],
    () => {
      const userPath = userType === 'Student'
        ? Routes.plato_api_student_path(userId)
        : Routes.plato_api_staffer_path(userId);

      return Axios.get(userPath).then(res => res.data);
    },
  );

  const isLoading = assignmentQuery.isLoading
    || studentQuestionAnswerQuery.isLoading
    || currentUserQuery.isLoading;

  const { setGradingStore } = useGradingStore();

  useEffect(() => {
    if (!isLoading) {
      setGradingStore({
        assignment,
        classroom,
        currentUser: currentUserQuery.data.staffer,
        isLoading,
        isQuestionMode: true,
        programId,
        questionAnswers: studentQuestionAnswerQuery.data?.data,
        questionId: parseInt(questionId, 10),
      });
    }
  }, [isLoading]);
};

export default QuestionGradingHooks;
